// file: `react/src/components/ChatWorkspace/ChatWorkspace.tsx`:

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Alert } from 'react-bootstrap';
import { MainContainer, Overlay, ChatContainer, MessageList, MessageSeparator } from '@chatscope/chat-ui-kit-react';
import { useUser } from '../UserContext.tsx';
import { useCrypto } from '../CryptoContext.tsx';
import { useChatSocket } from '../ChatSocketContext.tsx';
import UserSelect from '../UserSelect/UserSelect.tsx';
import AddImageOverlay from '../AddImageOverlay/AddImageOverlay.tsx';
import ChatscopeInbox from '../ChatscopeInbox/ChatscopeInbox.tsx';
import ChatscopeThread from '../ChatscopeThread/ChatscopeThread.tsx';
import ChatscopeDetails from '../ChatscopeDetails/ChatscopeDetails.tsx';
import { expressURL, authFetch } from '../../utils/networkUtils';
import { encryptMessageText } from '../../crypto/messagePayload.ts';
import { encryptPhotoForUpload } from '../../crypto/mediaPayload.ts';
import { threadAesKey, WrappedThreadKey } from '../../crypto/identity.ts';
import { decryptMessageList } from '../../utils/chatMessageDecrypt.ts';
import {
  mergeMessagesById,
  createClientMessageId,
  sendFingerprint,
  shouldApplyTextSendResult,
  isMyReaction,
  isSelfMessage
} from '../../utils/messageListUtils.ts';
import { readFetchError } from '../../utils/fetchError.ts';
import { buildReplyPayload } from '../../utils/replyPayload.ts';
import {
  markOutboxFailed,
  readOutboxEntry,
  removeOutboxEntry,
  upsertOutboxEntry
} from '../../utils/messageOutbox.ts';
import {
  migrateLegacyPrefs,
  markThreadReadOnServer,
  patchThreadPreferences,
  ThreadPrefKey,
  ThreadPrefs
} from '../threadPrefs.ts';
import {
  allowThreadInvites,
  effectiveRoomEncryption,
  ROOM_TYPES
} from '../threadRoomType.ts';
import type { RoomType } from '../threadRoomType.ts';
import { User } from '../UserContext.tsx';
import SchoolLabBanner from '../../school/SchoolLabBanner.tsx';
import { wrapPlaintextForSchoolSafety } from '../../school/safetyEnvelope.ts';
import { fetchSchoolSafetyKey } from '../../school/schoolApi.ts';
import {
  allowEmailLookupForRoom,
  canCreateClassmate,
  canCreateEncryptedChat,
  canCreateFamily,
  canCreateStaff,
  defaultCreateRoomType,
  showRoomTypePicker
} from '../../school/schoolClaims.ts';
import './ChatWorkspace.scss';
import '../ChatscopeThread/ChatscopeThread.scss';

type LoadMessagesOptions = {
  resync?: boolean;
};

const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function looksLikeEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 3 && EMAIL_LIKE.test(trimmed);
}

type FreshInvite = {
  path: string;
  emailsSent?: string[];
  mailUnsent?: boolean;
};

function defaultRoomType(user: User | null): RoomType {
  return defaultCreateRoomType(user);
}

function ChatWorkspace() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { users, activeUser, fetchAllUsers } = useUser();
  const { status, wrapNewThreadKeys, wrapThreadKeyForMembers, unwrapThreadKeyForSelf } = useCrypto();
  const { online, typing, sendTyping, subscribe, connectionStatus } = useChatSocket();

  const [threads, setThreads] = useState([]);
  const [threadsReady, setThreadsReady] = useState(false);
  const [threadsError, setThreadsError] = useState(null);
  const [messages, setMessages] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth > 768
  ));
  const [showNew, setShowNew] = useState(false);
  const [conversationName, setConversationName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [inviteEmailDraft, setInviteEmailDraft] = useState('');
  const [mintInviteLink, setMintInviteLink] = useState(false);
  const [freshInvite, setFreshInvite] = useState<FreshInvite | null>(null);
  const [newRoomType, setNewRoomType] = useState<RoomType>(ROOM_TYPES.ADULT_PRIVATE);
  const [encryptThread, setEncryptThread] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [threadQuery, setThreadQuery] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [draft, setDraft] = useState('');
  const [imageOpen, setImageOpen] = useState(false);
  const [lightbox, setLightbox] = useState('');
  const [threadEncrypted, setThreadEncrypted] = useState(false);
  const [threadKey, setThreadKey] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const threadKeyRef = useRef(null);
  const threadKeyBytesRef = useRef(null);
  const clientMessageIdRef = useRef<string | null>(null);
  const sendFingerprintRef = useRef('');
  const loadGen = useRef(0);
  const threadRef = useRef(null);
  const threadIdRef = useRef(threadId);
  const mountedRef = useRef(true);
  const sendTextRef = useRef<(text: string) => Promise<void>>(async () => {});
  const statusRef = useRef(status);
  statusRef.current = status;

  const thread = useMemo(
    () => (threads || []).find((item) => String(item.id) === String(threadId)) || null,
    [threads, threadId]
  );
  const activeThreadEncrypted = thread ? !!thread.encryptionEnabled : threadEncrypted;
  const encryptionLocked = activeThreadEncrypted && status !== 'unlocked';

  threadRef.current = thread;
  threadIdRef.current = threadId;

  const applyThreadPrefs = useCallback((threadId: string | number, prefs: ThreadPrefs) => {
    setThreads((prev) => (Array.isArray(prev)
      ? prev.map((item) => (String(item.id) === String(threadId) ? { ...item, ...prefs } : item))
      : prev));
  }, []);

  const fetchThreads = useCallback(() => {
    return authFetch(`${expressURL()}/api/get-threads`)
      .then(async (response) => {
        if (!response.ok) {
          const message = await readFetchError(response);
          throw new Error(message);
        }
        return response.json();
      })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setThreads(list);
        setThreadsError(null);
        setThreadsReady(true);
        migrateLegacyPrefs(list.map((item) => item.id)).catch(() => undefined);
      })
      .catch((error) => {
        console.error('Error fetching threads:', error);
        setThreadsError(error instanceof Error ? error.message : 'Could not load conversations');
        setThreadsReady(true);
      });
  }, []);

  const markThreadRead = useCallback(async (id: string | number) => {
    if (!id) return;
    const now = new Date().toISOString();
    applyThreadPrefs(id, { lastReadAt: now, unreadCount: 0 });
    try {
      const prefs = await markThreadReadOnServer(id);
      applyThreadPrefs(id, prefs);
      setActionError(null);
    } catch (error) {
      console.error('Failed to mark thread read:', error);
      fetchThreads();
      setActionError(error instanceof Error ? error.message : 'Could not update read state');
    }
  }, [applyThreadPrefs, fetchThreads]);

  const toggleThreadPref = useCallback(async (id: string | number, key: ThreadPrefKey) => {
    const current = (threads || []).find((item) => String(item.id) === String(id));
    const previous = !!current?.[key];
    applyThreadPrefs(id, { [key]: !previous });
    try {
      const prefs = await patchThreadPreferences(id, { [key]: !previous });
      applyThreadPrefs(id, prefs);
      setActionError(null);
    } catch (error) {
      console.error('Failed to update thread preference:', error);
      applyThreadPrefs(id, { [key]: previous });
      setActionError(error instanceof Error ? error.message : 'Could not update preference');
    }
  }, [applyThreadPrefs, threads]);

  const shareMissingThreadKeys = useCallback(async (current, keyBytes: Uint8Array) => {
    if (!current?.encryptionEnabled || !keyBytes) return;
    const wrapped = current.wrappedKeys || [];
    const missing = (current.members || []).filter((member) => (
      member?.cryptoPublicKey
      && !wrapped.some((item) => String(item.userId) === String(member.id))
    ));
    for (const member of missing) {
      try {
        const [wrappedKey] = await wrapThreadKeyForMembers(keyBytes, [{
          id: String(member.id),
          cryptoPublicKey: member.cryptoPublicKey
        }]);
        const response = await authFetch(`${expressURL()}/api/threads/${current.id}/members`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userId: member.id, wrappedKey })
        });
        if (response.ok) {
          const view = await response.json();
          setThreads((prev) => prev.map((item) => (
            String(item.id) === String(view.id) ? { ...item, ...view } : item
          )));
        }
      } catch (error) {
        console.error('Failed to wrap thread key for new member:', error);
      }
    }
  }, [wrapThreadKeyForMembers]);

  const loadMessages = useCallback(async (current, options: LoadMessagesOptions = {}) => {
    const { resync = false } = options;
    const gen = ++loadGen.current;
    if (!current) {
      setMessages([]);
      setThreadEncrypted(false);
      setHistoryError(null);
      threadKeyRef.current = null;
      threadKeyBytesRef.current = null;
      setThreadKey(null);
      return;
    }
    if (!resync) {
      setMessages(null);
      setHistoryError(null);
    }
    let key = null;
    const encryptionEnabled = !!current.encryptionEnabled;
    setThreadEncrypted(encryptionEnabled);
    if (encryptionEnabled && status === 'unlocked') {
      try {
        const raw = await unwrapThreadKeyForSelf((current.wrappedKeys || []) as WrappedThreadKey[]);
        if (gen !== loadGen.current) return;
        threadKeyBytesRef.current = raw;
        key = await threadAesKey(raw);
        threadKeyRef.current = key;
        setThreadKey(key);
      } catch (err) {
        console.error('Failed to unwrap thread key:', err);
        threadKeyRef.current = null;
        threadKeyBytesRef.current = null;
        setThreadKey(null);
      }
    } else {
      threadKeyRef.current = null;
      setThreadKey(null);
      if (!encryptionEnabled) threadKeyBytesRef.current = null;
    }

    try {
      const response = await authFetch(`${expressURL()}/api/db-multiple-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'bypass-tunnel-reminder': 'any_value'
        },
        body: JSON.stringify({ threadId: current.id })
      });
      if (!response.ok) {
        const message = await readFetchError(response);
        throw new Error(message);
      }
      const payload = await response.json();
      if (gen !== loadGen.current) return;
      const decrypted = await decryptMessageList(payload.db_messages || [], key);
      if (gen !== loadGen.current) return;
      setMessages((prev) => mergeMessagesById(prev || [], decrypted));
      setHistoryError(null);
      if (encryptionEnabled && key && threadKeyBytesRef.current) {
        void shareMissingThreadKeys(current, threadKeyBytesRef.current);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (gen !== loadGen.current) return;
      const message = error instanceof Error ? error.message : 'Could not load messages';
      setHistoryError(message);
      if (!resync) setMessages([]);
    }
  }, [status, unwrapThreadKeyForSelf, wrapThreadKeyForMembers, shareMissingThreadKeys]);

  const retryHistory = useCallback(() => {
    const current = threadRef.current;
    if (current) {
      loadMessages(current, { resync: true });
      return;
    }
    fetchThreads().then(() => {
      const resolved = threadRef.current;
      if (resolved) loadMessages(resolved);
    });
  }, [fetchThreads, loadMessages]);

  const prepareReplyPayload = useCallback(async () => {
    if (!replyTo) return undefined;
    if (activeThreadEncrypted && status !== 'unlocked') {
      throw new Error('Enter your login password to reply in this thread');
    }
    const key = threadKeyRef.current;
    if (activeThreadEncrypted && !key) {
      throw new Error('Enter your login password to reply in this thread');
    }
    return buildReplyPayload(replyTo, { encrypt: activeThreadEncrypted, key });
  }, [replyTo, activeThreadEncrypted, status]);

  const prepareCaption = useCallback(async (text: string) => {
    if (!activeThreadEncrypted) return text;
    if (status !== 'unlocked') {
      throw new Error('Enter your login password to send in this thread');
    }
    const key = threadKeyRef.current;
    if (!key) throw new Error('Enter your login password to send in this thread');
    return encryptMessageText(key, text.trim());
  }, [activeThreadEncrypted, status]);

  const preparePhoto = useCallback(async (file: File) => {
    if (!activeThreadEncrypted) return { file, mediaEncryption: null };
    if (status !== 'unlocked') {
      throw new Error('Enter your login password to send in this thread');
    }
    const key = threadKeyRef.current;
    if (!key) throw new Error('Enter your login password to send in this thread');
    return encryptPhotoForUpload(key, file);
  }, [activeThreadEncrypted, status]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  useEffect(() => {
    setEncryptThread(status === 'unlocked');
  }, [status]);

  useEffect(() => {
    setReplyTo(null);
    setThreadQuery('');
    setDraft('');
    setSendError(null);
    setHistoryError(null);
    setActionError(null);
    clientMessageIdRef.current = null;
    sendFingerprintRef.current = '';

    if (!activeUser?.id || !threadId) return;
    const outboxEntry = readOutboxEntry(activeUser.id, threadId);
    if (!outboxEntry) return;
    setDraft(outboxEntry.text);
    setReplyTo(outboxEntry.replyTo || null);
    clientMessageIdRef.current = outboxEntry.clientMessageId;
    sendFingerprintRef.current = outboxEntry.fingerprint;
    if (outboxEntry.status === 'failed') {
      setSendError('Could not send message');
    }
  }, [threadId, activeUser?.id]);

  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      setThreadEncrypted(false);
      return;
    }
    setMessages(null);
  }, [threadId]);

  useEffect(() => {
    if (!threadId) return;
    markThreadRead(threadId);
    if (thread) {
      loadMessages(thread);
    } else if (threadsReady) {
      setMessages([]);
      setThreadEncrypted(false);
      threadKeyRef.current = null;
      threadKeyBytesRef.current = null;
      setThreadKey(null);
      setHistoryError(threadsError || 'Conversation not found or unavailable');
    }
  }, [threadId, thread?.id, status, loadMessages, threadsReady, threadsError, markThreadRead]);

  useEffect(() => {
    let alive = true;
    const unsubscribe = subscribe(async (msg) => {
      if (msg.type === 'socket-open') {
        fetchThreads();
        const currentThread = threadRef.current;
        const currentThreadId = threadIdRef.current;
        if (currentThreadId && currentThread) {
          await loadMessages(currentThread, { resync: true });
          if (!alive) return;
          const userId = activeUser?.id;
          const encryptionLockedOnOpen = !!currentThread.encryptionEnabled && statusRef.current !== 'unlocked';
          if (userId && !encryptionLockedOnOpen) {
            const outboxEntry = readOutboxEntry(userId, currentThreadId);
            // Only auto-retry interrupted sends. Failed ones stay for the Retry button.
            if (outboxEntry && outboxEntry.status === 'queued') {
              setDraft(outboxEntry.text);
              setReplyTo(outboxEntry.replyTo || null);
              clientMessageIdRef.current = outboxEntry.clientMessageId;
              sendFingerprintRef.current = outboxEntry.fingerprint;
              await sendTextRef.current(outboxEntry.text);
            }
          }
        }
        return;
      }
      if (msg.type === 'presence' || msg.type === 'typing' || msg.type === 'typing-stop') return;
      if (msg.type === 'thread') {
        const eventThreadId = String(msg.threadId || msg.thread?.id || '');
        const viewing = eventThreadId && eventThreadId === String(threadIdRef.current);
        const removed = msg.removedUserId && String(msg.removedUserId) === String(activeUser?.id);
        if (msg.action === 'deleted') {
          if (activeUser?.id && eventThreadId) {
            removeOutboxEntry(activeUser.id, eventThreadId);
          }
          if (viewing) {
            setMessages(null);
            setDetailsOpen(false);
            navigate('/');
          }
          fetchThreads();
          return;
        }
        if (removed && viewing) {
          navigate('/');
        }
        fetchThreads();
        return;
      }
      if (msg.type === 'delete') {
        setMessages((prev) => (prev
          ? prev.filter((item) => String(item.id) !== String(msg.messageId))
          : prev));
        fetchThreads();
        return;
      }
      if (msg.type === 'emoji') {
        setMessages((prev) => (prev
          ? prev.map((item) => {
            if (String(item.id) !== String(msg.messageId)) return item;
            return { ...item, emoji: msg.emoji, reactions: msg.reactions || item.reactions };
          })
          : prev));
        return;
      }
      if (msg.threadId == null || msg.id == null) return;

      const isActiveThread = String(msg.threadId) === String(threadIdRef.current);
      const fromSelf = isSelfMessage(msg, activeUser?.id, activeUser?.shortName);

      setThreads((prev) => {
        const list = Array.isArray(prev) ? [...prev] : [];
        const index = list.findIndex((item) => String(item.id) === String(msg.threadId));
        if (index < 0) {
          fetchThreads();
          return prev;
        }
        const current = list[index];
        const unreadCount = isActiveThread || fromSelf
          ? 0
          : (typeof current.unreadCount === 'number' ? current.unreadCount : 0) + 1;
        const next = {
          ...current,
          unreadCount,
          lastMessage: {
            text: msg.text || '',
            time: msg.time || new Date().toISOString(),
            sender: msg.sender || '',
            senderId: msg.senderId || undefined,
            type: msg.type || 'text'
          },
          recent: [
            {
              time: msg.time || new Date().toISOString(),
              sender: msg.sender || '',
              senderId: msg.senderId || undefined
            },
            ...(current.recent || [])
          ].slice(0, 40)
        };
        list.splice(index, 1);
        list.unshift(next);
        return list;
      });

      if (!isActiveThread) return;
      const eventThreadId = msg.threadId;
      const key = threadKeyRef.current;
      const [decrypted] = await decryptMessageList([msg], key);
      if (!alive) return;
      if (String(eventThreadId) !== String(threadIdRef.current)) return;
      setMessages((prev) => mergeMessagesById(prev || [], [decrypted]));
      markThreadRead(eventThreadId);
    });
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [activeUser?.id, activeUser?.shortName, fetchThreads, loadMessages, markThreadRead, navigate, subscribe]);

  const handleReact = async (msg, picked) => {
    if (!activeUser?.shortName || !picked) return;
    const reactions = Array.isArray(msg.reactions) ? msg.reactions : [];
    const myReaction = reactions.find((r) => isMyReaction(r, activeUser.id, activeUser.shortName))?.emoji;
    const nextEmoji = myReaction === picked ? '-' : picked;
    try {
      const response = await authFetch(`${expressURL()}/api/store-emoji/${msg.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'bypass-tunnel-reminder': 'any_value'
        },
        body: JSON.stringify({ emoji: nextEmoji })
      });
      if (!response.ok) {
        const message = await readFetchError(response);
        throw new Error(message);
      }
      const result = await response.json();
      const nextReactions = Array.isArray(result.reactions) ? result.reactions : [];
      setMessages((prev) => (prev
        ? prev.map((item) => String(item.id) === String(msg.id) ? { ...item, reactions: nextReactions } : item)
        : prev));
      setActionError(null);
    } catch (error) {
      console.error('Error storing emoji:', error);
      setActionError(error instanceof Error ? error.message : 'Could not update reaction');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const response = await authFetch(`${expressURL()}/api/delete-message/${messageId}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const message = await readFetchError(response);
        throw new Error(message);
      }
      setMessages((prev) => (prev ? prev.filter((msg) => String(msg.id) !== String(messageId)) : []));
      setActionError(null);
    } catch (error) {
      console.error('handleDeleteMessage: error', error);
      setActionError(error instanceof Error ? error.message : 'Could not delete message');
    }
  };

  const sendText = async (text) => {
    const trimmed = (text || '').trim();
    if (!activeUser?.shortName || !trimmed || !threadId || sending) return;
    if (activeThreadEncrypted && status !== 'unlocked') return;

    const sendingThreadId = threadId;
    setSending(true);
    setSendError(null);
    let textToSend = trimmed;
    const fingerprint = sendFingerprint(trimmed, replyTo?.id);
    if (!clientMessageIdRef.current || sendFingerprintRef.current !== fingerprint) {
      clientMessageIdRef.current = createClientMessageId();
      sendFingerprintRef.current = fingerprint;
    }
    const clientMessageId = clientMessageIdRef.current;
    if (activeUser?.id) {
      upsertOutboxEntry(activeUser.id, sendingThreadId, {
        text: trimmed,
        replyTo: replyTo
          ? { id: replyTo.id, sender: replyTo.sender, text: replyTo.text }
          : null,
        clientMessageId,
        fingerprint,
        status: 'queued'
      });
    }
    try {
      const key = threadKeyRef.current;
      if (activeThreadEncrypted) {
        if (!key) throw new Error('Enter your login password to send in this thread');
        textToSend = await encryptMessageText(key, trimmed);
      }

      const replyPayload = await buildReplyPayload(replyTo, { encrypt: activeThreadEncrypted, key });

      let safetyEnvelope;
      if (thread?.roomType === ROOM_TYPES.SCHOOL_CLASSMATE) {
        const safetyKey = await fetchSchoolSafetyKey();
        safetyEnvelope = await wrapPlaintextForSchoolSafety(
          trimmed,
          safetyKey.schoolId,
          safetyKey.publicKeyJwk
        );
      }

      const response = await authFetch(`${expressURL()}/api/new-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'bypass-tunnel-reminder': 'any_value'
        },
        body: JSON.stringify({
          type: 'text',
          text: textToSend,
          threadId: sendingThreadId,
          clientMessageId,
          replyTo: replyPayload,
          ...(safetyEnvelope ? { safetyEnvelope } : {})
        })
      });
      if (!response.ok) {
        const message = await readFetchError(response);
        throw new Error(message);
      }
      const created = await response.json();
      const [decrypted] = await decryptMessageList([created], key);
      clientMessageIdRef.current = null;
      sendFingerprintRef.current = '';
      if (activeUser?.id) {
        removeOutboxEntry(activeUser.id, sendingThreadId);
      }

      if (
        mountedRef.current
        && shouldApplyTextSendResult(sendingThreadId, threadIdRef.current, created?.threadId)
      ) {
        setMessages((prev) => mergeMessagesById(prev || [], [decrypted]));
        setDraft('');
        setReplyTo(null);
        setSendError(null);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      if (activeUser?.id) {
        markOutboxFailed(activeUser.id, sendingThreadId);
      }
      if (
        mountedRef.current
        && String(sendingThreadId) === String(threadIdRef.current)
      ) {
        setSendError(err instanceof Error ? err.message : 'Could not send message');
      }
    } finally {
      if (mountedRef.current) setSending(false);
    }
  };
  sendTextRef.current = sendText;

  const retrySend = () => {
    if (draft.trim()) sendText(draft);
  };

  const addInviteEmail = (raw: string) => {
    const trimmed = raw.trim().toLowerCase();
    if (!looksLikeEmail(trimmed)) {
      setCreateError('Enter a valid email to invite.');
      return;
    }
    if (activeUser?.email && String(activeUser.email).toLowerCase() === trimmed) {
      setCreateError('You cannot invite your own email.');
      return;
    }
    setInviteEmails((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    setInviteEmailDraft('');
    setCreateError(null);
  };

  const handleCreateThread = async (event) => {
    event.preventDefault();
    setCreateError(null);
    const selectedIds = selectedUsers.map((user) => String(user.id));
    const uniqueUserIds = [...new Set(selectedIds.filter(Boolean))];
    const allowInvites = allowThreadInvites(newRoomType);
    const mintInvite = allowInvites && (inviteEmails.length > 0 || mintInviteLink);
    if (uniqueUserIds.length === 0 && !mintInvite) {
      setCreateError(
        allowInvites
          ? 'Select at least one participant, or invite someone by email.'
          : 'Select at least one participant'
      );
      return;
    }

    let encryptionEnabled = false;
    let wrappedKeys = [];
    let accessPeriodWraps = [];
    if (effectiveRoomEncryption(newRoomType, encryptThread)) {
      if (status !== 'unlocked') {
        setCreateError('Enter your login password before starting an encrypted conversation.');
        return;
      }
      const memberIds = [...new Set([...uniqueUserIds, String(activeUser?.id)])];
      const members = memberIds.map((id) => {
        const listed = users.find((u) => String(u.id) === id)
          || (String(id) === String(activeUser?.id) ? activeUser : null);
        const cryptoPublicKey = String(id) === String(activeUser?.id)
          ? (activeUser?.crypto?.publicKeyJwk || activeUser?.cryptoPublicKey || listed?.cryptoPublicKey)
          : listed?.cryptoPublicKey;
        return { id, cryptoPublicKey };
      });
      if (members.some((member) => !member.cryptoPublicKey)) {
        setCreateError('Every participant must turn on encryption in Settings first.');
        return;
      }
      try {
        const wrapped = await wrapNewThreadKeys(members, { roomType: newRoomType });
        wrappedKeys = wrapped.wrappedKeys;
        accessPeriodWraps = wrapped.accessPeriodWraps || [];
        encryptionEnabled = true;
      } catch (err) {
        setCreateError(err instanceof Error ? err.message : 'Could not encrypt this conversation');
        return;
      }
    }

    const response = await authFetch(`${expressURL()}/api/create-thread`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        threadName: conversationName,
        userIds: uniqueUserIds,
        roomType: newRoomType,
        encryptionEnabled,
        wrappedKeys,
        accessPeriodWraps,
        mintInvite,
        inviteEmails: allowInvites ? inviteEmails : []
      })
    });

    if (response.ok) {
      const created = await response.json();
      setShowNew(false);
      setConversationName('');
      setSelectedUsers([]);
      setInviteEmails([]);
      setInviteEmailDraft('');
      setMintInviteLink(false);
      setNewRoomType(defaultRoomType(activeUser));
      setEncryptThread(status === 'unlocked');
      if (created?.invite?.path) {
        setFreshInvite(created.invite);
        setDetailsOpen(true);
      }
      fetchThreads();
      if (created?.id) navigate(`/m/${created.id}`);
    } else {
      setCreateError(await readFetchError(response));
    }
  };

  const addMembers = async (picked: User[]) => {
    if (!thread) return;
    for (const person of picked) {
      let wrappedKey;
      if (thread.encryptionEnabled) {
        if (status !== 'unlocked' || !threadKeyBytesRef.current) {
          throw new Error('Unlock encryption before adding someone to this chat.');
        }
        const listed = users.find((u) => String(u.id) === String(person.id)) || person;
        const cryptoPublicKey = listed.cryptoPublicKey;
        if (!cryptoPublicKey) {
          throw new Error(`${person.fullName || person.shortName} has not turned on encryption.`);
        }
        const [wrapped] = await wrapThreadKeyForMembers(threadKeyBytesRef.current, [{
          id: String(person.id),
          cryptoPublicKey
        }]);
        wrappedKey = wrapped;
      }
      const response = await authFetch(`${expressURL()}/api/threads/${thread.id}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId: person.id, wrappedKey })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Could not add that person');
      }
      const view = await response.json();
      setThreads((prev) => prev.map((item) => String(item.id) === String(view.id) ? { ...item, ...view } : item));
    }
  };

  const removeMember = async (member) => {
    if (!thread || !member?.id) return;
    const self = String(member.id) === String(activeUser?.id) || member.shortName === activeUser?.shortName;
    if (!window.confirm(self ? 'Leave this conversation?' : `Remove ${member.fullName || member.shortName}?`)) return;
    const response = await authFetch(`${expressURL()}/api/threads/${thread.id}/members/${member.id}`, {
      method: 'DELETE'
    });
    if (!response.ok) return;
    if (self) navigate('/');
    else fetchThreads();
  };

  const renameThread = async (name: string) => {
    if (!thread) return;
    const response = await authFetch(`${expressURL()}/api/threads/${thread.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name })
    });
    if (!response.ok) throw new Error('Could not rename');
    const view = await response.json();
    setThreads((prev) => prev.map((item) => String(item.id) === String(view.id) ? { ...item, ...view } : item));
  };

  const deleteConversation = async () => {
    if (!thread) return;
    const deletingId = thread.id;
    const response = await authFetch(`${expressURL()}/api/threads/${deletingId}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Could not delete conversation');
    }
    if (activeUser?.id) {
      removeOutboxEntry(activeUser.id, deletingId);
    }
    setThreads((prev) => (Array.isArray(prev)
      ? prev.filter((item) => String(item.id) !== String(deletingId))
      : prev));
    setMessages(null);
    setDetailsOpen(false);
    navigate('/');
  };

  const adultPrivateSelected = newRoomType === ROOM_TYPES.ADULT_PRIVATE;
  const classmateSelected = newRoomType === ROOM_TYPES.SCHOOL_CLASSMATE;
  const createEncryptionLocked = adultPrivateSelected || classmateSelected;
  const newThreadEncryptionEnabled = effectiveRoomEncryption(newRoomType, encryptThread);
  const showTypePicker = showRoomTypePicker(activeUser);
  const showEncryptToggle = !createEncryptionLocked;
  const showInviteOnCreate = allowThreadInvites(newRoomType);
  const shellClass = [
    'chatscope-shell',
    threadId ? 'chatscope-shell--thread' : 'chatscope-shell--inbox',
    detailsOpen && threadId ? 'chatscope-shell--details' : ''
  ].filter(Boolean).join(' ');

  const newConversation = (
    <Form onSubmit={handleCreateThread}>
      <h2 className="h5 mb-3">New conversation</h2>
      <Form.Group className="mb-2">
        <Form.Label>To:</Form.Label>
        <UserSelect
          onChange={setSelectedUsers}
          excludeUserIds={activeUser?.id ? [activeUser.id] : []}
          allowEmailLookup={allowEmailLookupForRoom(newRoomType)}
          onUnknownEmail={showInviteOnCreate ? addInviteEmail : undefined}
        />
        <Form.Text>
          People who already have an account. Type an exact email if they are not in the list.
        </Form.Text>
      </Form.Group>
      {showInviteOnCreate && (
      <Form.Group className="mb-2">
        <Form.Label>Invite people without accounts:</Form.Label>
        <div className="d-flex gap-2">
          <Form.Control
            type="email"
            placeholder="friend@example.com"
            value={inviteEmailDraft}
            onChange={(e) => setInviteEmailDraft(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addInviteEmail(inviteEmailDraft);
              }
            }}
          />
          <Button
            type="button"
            variant="outline-secondary"
            onClick={() => addInviteEmail(inviteEmailDraft)}
          >
            Add
          </Button>
        </div>
        {inviteEmails.length > 0 && (
          <div className="mt-2 d-flex flex-wrap gap-2">
            {inviteEmails.map((email) => (
              <Button
                key={email}
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setInviteEmails((prev) => prev.filter((item) => item !== email))}
              >
                {email} ×
              </Button>
            ))}
          </div>
        )}
        <Form.Check
          className="mt-2"
          type="checkbox"
          id="mint-invite-link"
          label="Create an invite link I can copy"
          checked={mintInviteLink || inviteEmails.length > 0}
          onChange={(e) => setMintInviteLink(e.target.checked)}
          disabled={inviteEmails.length > 0}
        />
        <Form.Text>
          We email a join link when Resend is configured. They still sign up and confirm email.
          The same link is in thread details after you start.
        </Form.Text>
      </Form.Group>
      )}
      <Form.Group className="mb-2">
        <Form.Label>Conversation name:</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter name"
          value={conversationName}
          onChange={(e) => setConversationName(e.target.value)}
        />
      </Form.Group>
      {showTypePicker && (
      <Form.Group className="mb-3">
        <Form.Label>Conversation type:</Form.Label>
        {canCreateFamily(activeUser) && (
        <div className="border rounded p-2 mb-2">
          <Form.Check
            type="radio"
            name="conversation-room-type"
            id="room-type-family"
            label="Family chat"
            checked={newRoomType === ROOM_TYPES.FAMILY}
            onChange={() => setNewRoomType(ROOM_TYPES.FAMILY)}
          />
          <Form.Text>
            Everyday household conversation. Encryption is optional and this type is not included in adult recovery.
            The school cannot read it.
          </Form.Text>
        </div>
        )}
        {canCreateClassmate(activeUser) && (
          <div className="border rounded p-2 mb-2">
            <Form.Check
              type="radio"
              name="conversation-room-type"
              id="room-type-classmate"
              label="Classmate chat (Solbakken laboratory)"
              checked={newRoomType === ROOM_TYPES.SCHOOL_CLASSMATE}
              onChange={() => {
                setNewRoomType(ROOM_TYPES.SCHOOL_CLASSMATE);
                setEncryptThread(true);
              }}
            />
            <Form.Text>
              Closed group of similar-age pupils at this school. Teachers cannot open it. Pick from the roster, not by email.
              Messages are wrapped to each pupil and also to a laboratory school safety key — not recovery, not court Shamir, and not a wall against the municipality processor.
            </Form.Text>
          </div>
        )}
        {canCreateStaff(activeUser) && (
          <div className="border rounded p-2 mb-2">
            <Form.Check
              type="radio"
              name="conversation-room-type"
              id="room-type-staff"
              label="School staff note"
              checked={newRoomType === ROOM_TYPES.SCHOOL_STAFF}
              onChange={() => setNewRoomType(ROOM_TYPES.SCHOOL_STAFF)}
            />
            <Form.Text>
              Logged like school email: teacher–parent or teacher–pupil. Not a window into classmate chat.
            </Form.Text>
          </div>
        )}
        {canCreateEncryptedChat(activeUser) && (
        <div className="border rounded p-2">
          <Form.Check
            type="radio"
            name="conversation-room-type"
            id="room-type-encrypted"
            label="Encrypted chat"
            checked={adultPrivateSelected}
            onChange={() => {
              setNewRoomType(ROOM_TYPES.ADULT_PRIVATE);
              setEncryptThread(true);
            }}
          />
          <Form.Text>
            Always encrypted. Account recovery may expose the conversation as the recovering participant sees it.
          </Form.Text>
        </div>
        )}
      </Form.Group>
      )}
      {showEncryptToggle && (
      <Form.Group className="mb-2">
        <Form.Check
          type="checkbox"
          id="encrypt-thread"
          label="Encrypt this conversation"
          checked={newThreadEncryptionEnabled}
          onChange={(e) => setEncryptThread(e.target.checked)}
        />
      </Form.Group>
      )}
      {createError && <p className="text-danger mb-2">{createError}</p>}
      <Button variant="primary" type="submit" className="me-2">Start conversation</Button>
      <Button variant="secondary" type="button" onClick={() => setShowNew(false)}>Cancel</Button>
    </Form>
  );

  return (
    <div className="page-content chatscope-page">
      <SchoolLabBanner user={activeUser} />
      {freshInvite && (
        <Alert
          variant="info"
          className="mb-2"
          dismissible
          onClose={() => setFreshInvite(null)}
        >
          <div>Share this invite link. It expires in 7 days.</div>
          <code className="d-block text-break my-1">
            {typeof window !== 'undefined' ? `${window.location.origin}${freshInvite.path}` : freshInvite.path}
          </code>
          <Button
            size="sm"
            type="button"
            className="me-2"
            onClick={() => {
              const url = typeof window !== 'undefined'
                ? `${window.location.origin}${freshInvite.path}`
                : freshInvite.path;
              navigator.clipboard.writeText(url).catch(() => {});
            }}
          >
            Copy link
          </Button>
          {freshInvite.emailsSent && freshInvite.emailsSent.length > 0 && (
            <span className="small">Emailed {freshInvite.emailsSent.join(', ')}.</span>
          )}
          {freshInvite.mailUnsent && (
            <span className="small"> Email did not send; copy the link instead.</span>
          )}
        </Alert>
      )}
      <div className={shellClass}>
        <MainContainer responsive={Boolean(threadId)}>
          <ChatscopeInbox
            threads={threads}
            activeThreadId={threadId}
            myId={activeUser?.id}
            myName={activeUser?.shortName}
            online={online}
            typing={typing}
            onOpen={(id) => {
              navigate(`/m/${id}`);
            }}
            onNewConversation={() => {
              setNewRoomType(defaultRoomType(activeUser));
              setEncryptThread(status === 'unlocked');
              setCreateError(null);
              setInviteEmails([]);
              setInviteEmailDraft('');
              setMintInviteLink(false);
              setShowNew(true);
              fetchAllUsers();
            }}
            onTogglePin={(id) => {
              toggleThreadPref(id, 'pinned');
            }}
          />
          <div className="chatscope-chat-pane">
            {threadId ? (
              <ChatscopeThread
                data={messages}
                thread={thread}
                threadId={threadId}
                handleDeleteMessage={handleDeleteMessage}
                handleReact={handleReact}
                onSend={sendText}
                encryptionLocked={encryptionLocked}
                encrypted={activeThreadEncrypted}
                threadKey={threadKey}
                online={online}
                connectionStatus={connectionStatus}
                typingNames={typing[String(threadId)] || []}
                onBack={() => navigate('/')}
                detailsOpen={detailsOpen}
                onToggleDetails={() => setDetailsOpen((open) => !open)}
                threadQuery={threadQuery}
                replyTo={replyTo}
                onReply={setReplyTo}
                draft={draft}
                onDraft={setDraft}
                attachmentsDisabled={encryptionLocked}
                onAttach={() => setImageOpen(true)}
                onTyping={() => threadId && sendTyping(threadId)}
                onOpenImage={setLightbox}
                sendDisabled={encryptionLocked}
                sending={sending}
                sendError={sendError}
                onRetrySend={retrySend}
                historyError={historyError}
                onRetryHistory={retryHistory}
                actionError={actionError}
                onDismissActionError={() => setActionError(null)}
              />
            ) : (
              <ChatContainer>
                <MessageList>
                  {threadsError ? (
                    <MessageSeparator>
                      <div className="chatscope-thread-error">
                        <span>{threadsError}</span>
                        <button
                          type="button"
                          className="chatscope-thread-error__retry"
                          onClick={() => fetchThreads()}
                        >
                          Retry
                        </button>
                      </div>
                    </MessageSeparator>
                  ) : (
                    <MessageSeparator>Pick a conversation, or start a new one</MessageSeparator>
                  )}
                </MessageList>
              </ChatContainer>
            )}
          </div>
          {threadId && detailsOpen && thread && (
            <ChatscopeDetails
              key={thread.id}
              thread={thread}
              messages={messages || []}
              myId={activeUser?.id}
              myName={activeUser?.shortName}
              online={online}
              encryptionLocked={encryptionLocked}
              threadKey={threadKey}
              threadQuery={threadQuery}
              onThreadQuery={setThreadQuery}
              onClose={() => setDetailsOpen(false)}
              onRename={renameThread}
              onAddMembers={addMembers}
              onRemoveMember={removeMember}
              onTogglePin={() => { toggleThreadPref(thread.id, 'pinned'); }}
              onToggleMute={() => { toggleThreadPref(thread.id, 'muted'); }}
              onToggleArchive={() => { toggleThreadPref(thread.id, 'archived'); }}
              onDeleteConversation={deleteConversation}
              onOpenImage={setLightbox}
            />
          )}
        </MainContainer>
      </div>
      {showNew && (
        <Overlay className="chatscope-new-thread-overlay">
          {newConversation}
        </Overlay>
      )}
      <AddImageOverlay
        message={draft}
        isVisible={imageOpen && !encryptionLocked}
        onClose={() => setImageOpen(false)}
        refreshMessages={() => {
          setReplyTo(null);
          setDraft('');
          if (thread) loadMessages(thread, { resync: true });
        }}
        threadId={threadId}
        encrypted={activeThreadEncrypted}
        buildReplyPayload={prepareReplyPayload}
        prepareCaption={prepareCaption}
        preparePhoto={preparePhoto}
      />
      {lightbox && (
        <Overlay className="chatscope-lightbox" onClick={() => setLightbox('')}>
          <img src={lightbox} alt="" onClick={(e) => e.stopPropagation()} />
        </Overlay>
      )}
    </div>
  );
}

export default ChatWorkspace;
